import { Fragment, useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  Link,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  ActionType,
  ClientIntegrationType,
  GetClientIntegrationDocument,
  GetIntegrationsSummaryDocument,
  GetTicketContextDocument,
  IntegrationType,
  useCreateClientIntegrationMutation,
  useTriggerActionLazyQuery,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { getError } from "~/lib/handle-error";
import { IntegrationAddHeader } from "../../../headers/IntegrationAddHeader";
import { UpdateFlowData } from "../../../utils/integrationsTypes";
import useGenerateIntegrationName from "../../../utils/integrationsUtils";
import { Command } from "~/components/guides/components";
import {
  GITHUB_ISSUES_PERSONAL_ACCESS_TOKEN_ERROR_MESSAGE,
  GITHUB_ISSUES_PERSONAL_ACCESS_TOKEN_PATTERN,
  helperTextStyles,
  isNotGithubUrl,
  isUrl,
  ValidationMessage,
} from "~/pages/integrations/utils/integrationValidationUtils";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { JiraAutoConfigurationSection } from "~/pages/integrations/hosted-integrations/forms/jira/JiraAutoConfigurationSection";
import { motion } from "motion/react";
import { SubField } from "~/components/Form/components";
import { Flex } from "~/components/Flex";
import { PasswordField } from "~/components/Form/components/PasswordField";
import { setDocumentTitle } from "~/lib/commonUtils";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

export type GithubIssueFormInput = {
  integrationName: string;
  enterpriseUrl: string;
  token: string;
  defaultRepoOwner: string;
  defaultRepoName: string;
  autoCloseTickets: boolean;
  autoCreateTickets: boolean;
};

const defaultValues: GithubIssueFormInput = {
  integrationName: "",
  enterpriseUrl: "",
  token: "",
  defaultRepoOwner: "",
  defaultRepoName: "",
  autoCloseTickets: false,
  autoCreateTickets: false,
};

export function GithubIssuesIntegrationForm({
  scope,
  updateFlow,
}: {
  scope: SpaceScope | OrganizationScope;
  updateFlow?: UpdateFlowData;
}) {
  const [searchParams] = useSearchParams();
  const redirectToParam = searchParams.get("redirectTo");
  let navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const defaultIntegrationName = useGenerateIntegrationName({ scope });
  const theme = useTheme();

  const {
    control,
    handleSubmit,
    reset,
    getFieldState,
    formState: { errors, isValid, isSubmitSuccessful },
    watch,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      ...defaultValues,
      integrationName: defaultIntegrationName,
    },
  });

  const [watchAutoCreateOnDrift] = watch(["autoCreateTickets"]);

  const [createIntegration] = useCreateClientIntegrationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const integrationMrn = data.createClientIntegration.integration.mrn;
      triggerClientIntegrationScan({
        variables: { input: { mrn: integrationMrn, type: ActionType.RunScan } },
      });
    },
    refetchQueries: [
      GetTicketContextDocument,
      {
        query: GetIntegrationsSummaryDocument,
        variables: { input: { spaceMrn: scope.mrn } },
      },
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const [triggerClientIntegrationScan] = useTriggerActionLazyQuery({
    onError(error) {
      console.log("%c Error Scheduling scan on creation", "color: tomato");
      console.log(error.message);
    },
  });

  useEffect(() => {
    if (updateFlow) {
      const configOptions = updateFlow.integration.configurationOptions;
      if (configOptions?.__typename !== "GithubTicketingConfigurationOptions")
        return;

      reset({
        integrationName: updateFlow.integration.name,
        enterpriseUrl: configOptions.enterpriseUrl || "",
        defaultRepoOwner: configOptions.defaultRepoOwner || "",
        defaultRepoName: configOptions.defaultRepoName || "",
        autoCloseTickets: configOptions.autoCloseTickets,
        autoCreateTickets: configOptions.autoCreateTickets,
      });
    }
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful && !updateFlow) {
      reset(defaultValues);
    }
  }, [isSubmitSuccessful]);

  const onSubmit: SubmitHandler<GithubIssueFormInput> = async (data) => {
    const githubTicketingConfigurationOptions = {
      enterpriseUrl: data.enterpriseUrl.trim(),
      token: data.token?.trim?.() || "",
      defaultRepoOwner: data.defaultRepoOwner,
      defaultRepoName: data.defaultRepoName,
      autoCloseTickets: data.autoCloseTickets,
      autoCreateTickets: data.autoCreateTickets,
    };

    const integrationName =
      data.integrationName?.trim() || updateFlow?.integration.name;

    try {
      if (updateFlow) {
        await updateIntegration({
          variables: {
            input: {
              name: integrationName,
              mrn: getIntegrationMrn({
                scope,
                integrationId:
                  updateFlow?.integration.mrn.split("/").pop() || "",
              }),
              type: ClientIntegrationType.TicketSystemGithub,
              configurationOptions: {
                githubTicketingConfigurationOptions,
              },
            },
          },
        });
        const integrationId = updateFlow?.integration.mrn.split("/").pop();
        enqueueSnackbar("Successfully updated configuration", {
          variant: "success",
        });

        navigate(
          `/space/integrations/ticket_system_github/${integrationId}/?${scope.params}`,
        );
      } else {
        await createIntegration({
          variables: {
            input: {
              spaceMrn: scope.mrn,
              name: data.integrationName.trim(),
              type: ClientIntegrationType.TicketSystemGithub,
              longLivedToken: false,
              configurationOptions: {
                githubTicketingConfigurationOptions,
              },
            },
          },
        });
      }
      if (redirectToParam) {
        navigate(redirectToParam);
      } else {
        navigate(`/space/integrations/ticket_system_github?${scope.params}`);
      }
    } catch (e) {
      const msg = getError(e);
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  setDocumentTitle(["GitHub Issues", "Integrations Setup"]);

  return (
    <Fragment>
      <IntegrationAddHeader {...{ type: IntegrationType.TicketSystemGithub }} />
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <>
            {/* Step 1 */}
            <Box pb={4}>
              <Command
                number={1}
                options={{
                  fontSize: { xs: 16 },
                  dotColor: theme.palette.background.lightest,
                }}
              >
                Choose an integration name
              </Command>

              <Box pl={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2, mt: 2 }}
                >
                  Please choose a descriptive name that lets you easily identify
                  your integration.
                </Typography>
                <Controller
                  name="integrationName"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      sx={{
                        background: (theme) => theme.palette.code.background,
                        borderRadius: 1,
                        color: "text.primary",
                        ...helperTextStyles,
                      }}
                      placeholder="Your integration name..."
                      error={Boolean(errors.integrationName)}
                      helperText={
                        Boolean(errors.integrationName) && (
                          <ValidationMessage error={errors.integrationName} />
                        )
                      }
                    />
                  )}
                />
              </Box>
            </Box>

            {/* Step 2 */}
            <Box pb={4}>
              <Command
                number={2}
                options={{
                  fontSize: { xs: 16 },
                  dotColor: theme.palette.background.lightest,
                }}
              >
                Provide your GitHub Enterprise Server URL{" "}
                <Typography
                  component="span"
                  sx={{ color: "text.secondary", fontStyle: "italic" }}
                >
                  (optional)
                </Typography>
              </Command>

              <Box pl={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2, mt: 2 }}
                >
                  If you use a dedicated GitHub Enterprise server instead of
                  github.com, specify the URL. To learn more, read the{" "}
                  <Link
                    href="https://mondoo.com/docs/ticketing/setup-github"
                    target="_blank"
                    rel="noopener"
                  >
                    Mondoo documentation
                  </Link>
                  .
                </Typography>
                <Controller
                  name="enterpriseUrl"
                  control={control}
                  rules={{
                    required: false,
                    validate: {
                      isUrl: (value) =>
                        !value ||
                        isUrl(
                          value,
                          "This is not a valid URL. Please enter a valid GitHub Enterprise Server URL.",
                        ),
                      isNotGithubUrl: (value) =>
                        !value || isNotGithubUrl(value),
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      sx={{
                        background: theme.palette.code.background,
                        borderRadius: 1,
                        color: "text.primary",
                        ...helperTextStyles,
                      }}
                      placeholder="Your GitHub Enterprise URL..."
                      error={Boolean(errors.enterpriseUrl)}
                      helperText={
                        Boolean(errors.enterpriseUrl) && (
                          <ValidationMessage error={errors.enterpriseUrl} />
                        )
                      }
                    />
                  )}
                />
              </Box>
            </Box>
            {/*  Step 3 */}
            <Box pb={4}>
              <Command
                number={3}
                options={{
                  fontSize: { xs: 16 },
                  dotColor: theme.palette.background.lightest,
                }}
              >
                Provide your personal access token
              </Command>

              <Box pl={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2, mt: 2 }}
                >
                  To learn about creating a personal access token, read the{" "}
                  <Link
                    href="https://mondoo.com/docs/ticketing/setup-github"
                    target="_blank"
                    rel="noopener"
                  >
                    Mondoo documentation
                  </Link>
                  .
                </Typography>
                <Controller
                  name="token"
                  control={control}
                  rules={{
                    required: !updateFlow,
                    pattern: {
                      value: GITHUB_ISSUES_PERSONAL_ACCESS_TOKEN_PATTERN,
                      message:
                        GITHUB_ISSUES_PERSONAL_ACCESS_TOKEN_ERROR_MESSAGE,
                    },
                  }}
                  render={({ field }) => (
                    <PasswordField
                      {...field}
                      fullWidth
                      sx={{
                        background: theme.palette.code.background,
                        borderRadius: 1,
                        color: "text.primary",
                        ...helperTextStyles,
                      }}
                      placeholder={"xxxx-xxxxxxxxxxxx-xxxx"}
                      error={
                        getFieldState("token").isTouched &&
                        Boolean(errors.token)
                      }
                      helperText={
                        Boolean(errors.token) &&
                        getFieldState("token").isTouched && (
                          <ValidationMessage error={errors.token} />
                        )
                      }
                    />
                  )}
                />
              </Box>
            </Box>
            {/* Step 4 */}
            <Box pb={4}>
              <Command
                number={4}
                options={{
                  fontSize: { xs: 16 },
                  dotColor: theme.palette.background.lightest,
                }}
              >
                Configure preferences
              </Command>
              <Box mt={3} pl={6}>
                <JiraAutoConfigurationSection
                  control={control}
                  ticketType={IntegrationType.TicketSystemGithub}
                  showAutoCreateSection={true}
                  autoCreateSubField={
                    <Box
                      component={motion.div}
                      initial={false}
                      transition={{
                        duration: 0.35,
                        staggerChildren: 0.25,
                      }}
                      sx={{ overflow: "hidden" }}
                    >
                      <SubField
                        configuration="Select a drift organization and repository"
                        caption="Specify the GitHub organization and repository in which to create an issue for each new drift case."
                        disabled={!watchAutoCreateOnDrift}
                        field={
                          <Flex flex="1 0 auto" gap={2} alignItems="flex-end">
                            <Controller
                              name="defaultRepoOwner"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  disabled={!watchAutoCreateOnDrift}
                                  sx={{
                                    background: theme.palette.code.background,
                                    borderRadius: 1,
                                    color: "text.primary",
                                    ...helperTextStyles,
                                  }}
                                  placeholder={"Your GitHub organization..."}
                                  error={
                                    getFieldState("defaultRepoOwner")
                                      .isTouched &&
                                    Boolean(errors.defaultRepoOwner)
                                  }
                                  helperText={
                                    Boolean(errors.defaultRepoOwner) &&
                                    getFieldState("defaultRepoOwner")
                                      .isTouched && (
                                      <ValidationMessage
                                        error={errors.defaultRepoOwner}
                                      />
                                    )
                                  }
                                />
                              )}
                            />
                            <Controller
                              name="defaultRepoName"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  disabled={!watchAutoCreateOnDrift}
                                  sx={{
                                    background: theme.palette.code.background,
                                    borderRadius: 1,
                                    color: "text.primary",
                                    ...helperTextStyles,
                                  }}
                                  placeholder={"Your GitHub repository..."}
                                  error={
                                    getFieldState("defaultRepoName")
                                      .isTouched &&
                                    Boolean(errors.defaultRepoName)
                                  }
                                  helperText={
                                    Boolean(errors.defaultRepoName) &&
                                    getFieldState("defaultRepoName")
                                      .isTouched && (
                                      <ValidationMessage
                                        error={errors.defaultRepoName}
                                      />
                                    )
                                  }
                                />
                              )}
                            />
                          </Flex>
                        }
                      />
                    </Box>
                  }
                />
              </Box>
            </Box>
            <Box width={1}>
              <Divider />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "end", mt: 5 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                endIcon={<ChevronRightIcon />}
                // setValue doesn't trigger the required validation so we're doing it manually here
                disabled={!isValid}
              >
                {updateFlow ? "update configuration" : "create integration"}
              </Button>
            </Box>
          </>
        </form>
      </Box>
    </Fragment>
  );
}
